// "Add shipping fees" block shown when moving a student to Aramex whose
// invoice has no shipping fee. Cash needs only the fee; a bank transfer also
// needs the payer's full name and phone number.

export const EMPTY_SHIPPING = {
  enabled: false,
  shipping_fee: "",
  shipping_payment_method: "",
  shipping_payer_name: "",
  shipping_payer_phone: "",
};

// Mirrors USD_CATEGORIES in Server/src/utils/pricing.js.
const USD_CATEGORIES = ["outside", "outsideA", "foreignUsd"];
export const shippingCurrency = (category) =>
  USD_CATEGORIES.includes(category) ? "USD" : "EGP";

// Standard shipping fee: 600 EGP, or 10 USD for USD categories. Ticked by
// default; the amount stays editable.
export const defaultShipping = (category) => ({
  ...EMPTY_SHIPPING,
  enabled: true,
  shipping_fee: shippingCurrency(category) === "USD" ? "10" : "600",
});

// Same rules as the server. Returns an error message, or "".
export const validateShipping = (s) => {
  if (!s.enabled) return "";
  if (!(Number(s.shipping_fee) > 0)) return "Enter the shipping fee amount.";
  if (s.shipping_payment_method !== "cash" && s.shipping_payment_method !== "bank_transfer") {
    return "Choose how the shipping fee was paid.";
  }
  if (s.shipping_payment_method === "bank_transfer") {
    if (!s.shipping_payer_name.trim()) return "Payer full name is required for a bank transfer.";
    if (!/^\+?\d{7,15}$/.test(s.shipping_payer_phone.replace(/[\s-]/g, ""))) {
      return "Enter a valid payer phone number.";
    }
  }
  return "";
};

// Request body fields, or {} when the box is unticked.
export const shippingPayload = (s) => {
  if (!s.enabled) return {};
  const isTransfer = s.shipping_payment_method === "bank_transfer";
  return {
    shipping_fee: s.shipping_fee,
    shipping_payment_method: s.shipping_payment_method,
    shipping_payer_name: isTransfer ? s.shipping_payer_name.trim() : "",
    shipping_payer_phone: isTransfer ? s.shipping_payer_phone.replace(/[\s-]/g, "") : "",
  };
};

export default function AramexShippingFields({ value, onChange, idPrefix = "aramex", currency }) {
  const set = (name) => (e) =>
    onChange({ ...value, [name]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div className="text-left">
      <label className="flex items-center gap-2 font-semibold cursor-pointer">
        <input
          type="checkbox"
          checked={value.enabled}
          onChange={set("enabled")}
          className="w-4 h-4 accent-blue-600"
        />
        Add shipping fees
      </label>

      {value.enabled && (
        <div className="mt-3 p-3 border rounded-lg bg-blue-50 flex flex-col gap-2">
          <label className="text-sm font-semibold">
            Shipping Fee <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="Amount"
              value={value.shipping_fee}
              onChange={set("shipping_fee")}
              className="p-2 border rounded-lg flex-1"
            />
            {currency && <span className="text-sm font-semibold text-gray-600">{currency}</span>}
          </div>

          <span className="text-sm font-semibold">
            Shipping Payment Method <span className="text-red-500">*</span>
          </span>
          <div className="flex gap-6 text-sm">
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name={`${idPrefix}_shipping_method`}
                value="cash"
                checked={value.shipping_payment_method === "cash"}
                onChange={set("shipping_payment_method")}
              />
              Cash
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name={`${idPrefix}_shipping_method`}
                value="bank_transfer"
                checked={value.shipping_payment_method === "bank_transfer"}
                onChange={set("shipping_payment_method")}
              />
              Bank Transfer
            </label>
          </div>

          {value.shipping_payment_method === "bank_transfer" && (
            <>
              <label className="text-sm font-semibold">
                Payer Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                maxLength={255}
                value={value.shipping_payer_name}
                onChange={set("shipping_payer_name")}
                className="p-2 border rounded-lg"
              />
              <label className="text-sm font-semibold">
                Payer Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                maxLength={20}
                placeholder="e.g. 01012345678"
                value={value.shipping_payer_phone}
                onChange={set("shipping_payer_phone")}
                className="p-2 border rounded-lg"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
